(() => {
  window.TC = window.TC || {};
  if (window.TC.infiniteScroll?.refresh) {
    window.TC.infiniteScroll.refresh();
    return;
  }

  const nextSelector = 'a.pagination__next, a[rel="next"]';
  const navigationSelector = '.link-dropdown__link, .filter-group__applied-item, .filter-group__clear-link, .pagination a';
  let instance = null;
  let suspendedGrid = null;
  let observer = null;
  let queued = false;
  let destroyed = false;

  function context(root = document) {
    const container = root.querySelector('filter-container');
    const section = container?.closest('.shopify-section');
    const grid = container?.querySelector('.product-grid');
    const pagination = section?.querySelector('.pagination-row');
    return section && grid && pagination ? { section, grid, pagination } : null;
  }

  function pageKey(href) {
    const url = new URL(href, window.location.href);
    url.hash = '';
    url.searchParams.sort();
    return url.href;
  }

  function productKey(item) {
    if (item.dataset.productId) return `id:${item.dataset.productId}`;
    const href = item.querySelector('a[href*="/products/"]')?.getAttribute('href');
    return href ? `path:${new URL(href, window.location.href).pathname.split('/products/')[1]}` : null;
  }

  function products(grid) {
    return Array.from(grid.children).filter(item => item.matches('product-block, .product-block'));
  }

  class Pagination {
    constructor(current) {
      Object.assign(this, current);
      this.destroyed = false;
      this.loading = false;
      this.failed = false;
      this.visited = new Set([pageKey(window.location.href)]);
      this.nextUrl = this.readNext();
    }

    readNext() {
      const href = this.pagination.querySelector(nextSelector)?.getAttribute('href');
      if (!href || href === '#') return null;
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return null;
      return this.visited.has(pageKey(url.href)) ? null : url.href;
    }

    current() {
      const active = context();
      return !this.destroyed && !suspendedGrid && active?.grid === this.grid
        && active.pagination === this.pagination && !this.section.querySelector('[data-ajax-container].ajax-loading');
    }

    check() {
      if (!this.current() || this.loading || this.failed || !this.nextUrl) return;
      if (this.pagination.getBoundingClientRect().top <= window.innerHeight + 1200) this.load();
    }

    async load() {
      if (!this.current() || this.loading || this.failed || !this.nextUrl) return;
      this.loading = true;
      const requestedUrl = this.nextUrl;
      const requestKey = pageKey(requestedUrl);
      this.abortController = new AbortController();
      this.loader = document.createElement('div');
      this.loader.className = 'rittencom-infinite-scroll-loader';
      this.loader.setAttribute('role', 'status');
      this.loader.setAttribute('aria-live', 'polite');
      const label = document.createElement('div');
      label.className = 'rittencom-infinite-scroll-spinner';
      label.textContent = this.pagination.querySelector(nextSelector)?.textContent || '';
      this.loader.appendChild(label);
      this.pagination.before(this.loader);
      this.previousBusy = this.grid.getAttribute('aria-busy');
      this.grid.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestedUrl, {
          credentials: 'same-origin',
          signal: this.abortController.signal
        });
        if (!response.ok) throw new Error(`Pagination HTTP ${response.status}`);
        const html = await response.text();
        if (!this.current()) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = this.section.id ? doc.getElementById(this.section.id) : doc;
        const incoming = section && context(section);
        if (!incoming) throw new Error('Pagination response has no matching collection grid');

        const seen = new Set(products(this.grid).map(productKey).filter(Boolean));
        const fragment = document.createDocumentFragment();
        let itemsLoaded = 0;
        for (const item of products(incoming.grid)) {
          const key = productKey(item);
          if (!key) throw new Error('Pagination product has no identity');
          if (seen.has(key)) continue;
          seen.add(key);
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.removeAttribute('data-cascade');
          item.style.removeProperty('--animation-order');
          fragment.appendChild(item);
          itemsLoaded += 1;
        }

        this.grid.appendChild(fragment);
        this.visited.add(requestKey);
        this.pagination.innerHTML = incoming.pagination.innerHTML;
        this.nextUrl = this.readNext();
        if (!this.nextUrl) this.pagination.replaceChildren();
        document.dispatchEvent(new CustomEvent('rittencom:infinite-scroll:loaded', {
          detail: { requestedUrl, itemsLoaded, nextUrl: this.nextUrl }
        }));
      } catch (error) {
        if (!this.destroyed && error.name !== 'AbortError') {
          this.failed = true;
          console.warn('[RittencomInfiniteScroll]', error);
        }
      } finally {
        this.loading = false;
        this.clearLoading();
        if (this.current() && !this.failed) schedule();
      }
    }

    clearLoading() {
      this.loader?.remove();
      this.loader = null;
      if (this.previousBusy !== undefined) {
        if (this.previousBusy === null) this.grid.removeAttribute('aria-busy');
        else this.grid.setAttribute('aria-busy', this.previousBusy);
        this.previousBusy = undefined;
      }
    }

    destroy() {
      this.destroyed = true;
      this.abortController?.abort();
      this.clearLoading();
    }
  }

  function suspend() {
    suspendedGrid = suspendedGrid || context()?.grid || null;
    instance?.destroy();
    instance = null;
  }

  function refresh() {
    if (destroyed) return;
    const current = context();
    if (!current) {
      instance?.destroy();
      instance = null;
      return;
    }
    if (current.section.querySelector('[data-ajax-container].ajax-loading')) {
      suspend();
      return;
    }
    if (suspendedGrid === current.grid) return;
    suspendedGrid = null;
    if (instance?.grid !== current.grid || instance?.pagination !== current.pagination) {
      instance?.destroy();
      instance = new Pagination(current);
    }
    instance.check();
  }

  function schedule() {
    if (destroyed || queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      refresh();
    });
  }

  function filterIntent(event) {
    const current = context();
    if (!current || !(event.target instanceof Element) || !current.section.contains(event.target)) return;
    if (event.type === 'click') {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest(navigationSelector);
      if (!link || link.getAttribute('href') === '#') return;
    } else if (!event.target.closest('#CollectionFilterForm')) return;
    suspend();
  }

  function sectionUnload(event) {
    if (instance && event.target.contains(instance.grid)) suspend();
  }

  function boot() {
    if (destroyed || observer) return;
    observer = new MutationObserver(schedule);
    observer.observe(document.querySelector('main') || document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['class']
    });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('popstate', suspend);
    document.addEventListener('change', filterIntent, true);
    document.addEventListener('submit', filterIntent, true);
    document.addEventListener('click', filterIntent, true);
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:section:unload', sectionUnload);
    refresh();
  }

  window.TC.infiniteScroll = {
    refresh,
    destroy() {
      destroyed = true;
      instance?.destroy();
      observer?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('popstate', suspend);
      document.removeEventListener('change', filterIntent, true);
      document.removeEventListener('submit', filterIntent, true);
      document.removeEventListener('click', filterIntent, true);
      document.removeEventListener('shopify:section:load', schedule);
      document.removeEventListener('shopify:section:unload', sectionUnload);
      document.removeEventListener('DOMContentLoaded', boot);
      delete window.TC.infiniteScroll;
    }
  };
  window.scheduleRittencomInfiniteScrollInit = refresh;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
